/**
 * Utility functions for managing and cleaning up localStorage
 * with mobile-specific optimizations
 */

import { createLogger } from './logger';
import { getCurrentGameTimestamp } from '../config/dateTimeConfig';

const WALLSTREETLE_PREFIX = 'wallstreetle_';
const STOCK_OPTIONS_KEY_PREFIX = 'wallstreetle_stock_options_';

/**
 * Detect if user is on a mobile device
 * Uses both viewport size and user agent for accuracy
 */
export const isMobileDevice = (): boolean => {
  // Check user agent for mobile patterns
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const mobileUserAgents = /iPhone|iPad|iPod|Android|webOS|BlackBerry|Windows Phone/i;
  const isMobileUserAgent = mobileUserAgents.test(userAgent);

  // Check viewport size (mobile typically <= 768px width)
  const isMobileViewport = typeof window !== 'undefined' && window.innerWidth <= 768;

  // Consider it mobile if either condition is true
  return isMobileUserAgent || isMobileViewport;
};

// Storage limits differ between mobile and desktop
const MAX_STORAGE_SIZE_MB_DESKTOP = 5; // Desktop browsers: 5-10MB
const MAX_STORAGE_SIZE_MB_MOBILE = 3;  // Mobile browsers: More conservative due to iOS Safari 5MB limit

// Quota warning thresholds
const QUOTA_WARNING_THRESHOLD_DESKTOP = 0.8; // 80% on desktop
const QUOTA_WARNING_THRESHOLD_MOBILE = 0.6;  // 60% on mobile (more aggressive)

// Get appropriate thresholds based on device
const getStorageLimits = () => {
  const isMobile = isMobileDevice();
  return {
    maxSizeMB: isMobile ? MAX_STORAGE_SIZE_MB_MOBILE : MAX_STORAGE_SIZE_MB_DESKTOP,
    warningThreshold: isMobile ? QUOTA_WARNING_THRESHOLD_MOBILE : QUOTA_WARNING_THRESHOLD_DESKTOP,
    isMobile
  };
};

const MAX_STORAGE_SIZE_MB = getStorageLimits().maxSizeMB;

const storageLogger = createLogger('StorageCleanup', { flag: 'CACHE' });

const logStorageEvent = (
  level: 'debug' | 'info' | 'warn' | 'error',
  message: string,
  details?: Record<string, unknown>
) => {
  if (!storageLogger.isEnabled()) {
    return;
  }

  const payload = details ? [message, details] : [message];

  switch (level) {
    case 'info':
      storageLogger.info(...payload);
      break;
    case 'warn':
      storageLogger.warn(...payload);
      break;
    case 'error':
      storageLogger.error(...payload);
      break;
    default:
      storageLogger.debug(...payload);
  }
};

const logStorageBatch = (key: string, entry: Record<string, unknown>) => {
  storageLogger.batch<Record<string, unknown>>(key, entry, {
    flushMs: 200,
    header: (entries) => `🧹 [StorageCleanup] ${entries.length} ${key} event(s)`
  });
};

/**
 * Get the approximate size of localStorage in bytes
 */
export const getLocalStorageSize = (): number => {
  let total = 0;
  try {
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        total += (localStorage[key].length + key.length) * 2; // *2 for UTF-16 encoding
      }
    }
  } catch (error) {
    logStorageEvent('warn', 'Error calculating localStorage size', {
      message: error instanceof Error ? error.message : 'Unknown error'
    });
  }
  return total;
};

/**
 * Get the size of localStorage in MB
 */
export const getLocalStorageSizeMB = (): number => {
  return getLocalStorageSize() / (1024 * 1024);
};

/**
 * Check if localStorage is approaching quota
 * Uses mobile-aware thresholds: 60% on mobile, 80% on desktop
 */
export const isStorageNearQuota = (): boolean => {
  const size = getLocalStorageSize();
  const limits = getStorageLimits();
  const maxBytes = limits.maxSizeMB * 1024 * 1024;
  return size > maxBytes * limits.warningThreshold;
};

/**
 * Get all Wallstreetle-related localStorage keys
 */
export const getWallstreetleKeys = (): string[] => {
  const keys: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(WALLSTREETLE_PREFIX)) {
        keys.push(key);
      }
    }
  } catch (error) {
    logStorageEvent('warn', 'Error retrieving Wallstreetle keys', {
      message: error instanceof Error ? error.message : 'Unknown error'
    });
  }
  return keys;
};

/**
 * Get the size of a specific localStorage key in bytes
 */
export const getKeySize = (key: string): number => {
  try {
    const value = localStorage.getItem(key);
    if (value) {
      return (key.length + value.length) * 2; // *2 for UTF-16 encoding
    }
  } catch (error) {
    logStorageEvent('warn', 'Error getting key size', {
      key,
      message: error instanceof Error ? error.message : 'Unknown error'
    });
  }
  return 0;
};

/**
 * Remove old game states (keep only current day's category states)
 * Cleans up both legacy keys and category-specific game states from previous days
 */
export const cleanupOldGameStates = (): number => {
  let removedCount = 0;
  try {
    const today = getCurrentGameTimestamp();

    // Remove legacy single game state if it exists
    const legacyKey = 'wallstreetle_gameState';
    if (localStorage.getItem(legacyKey)) {
      localStorage.removeItem(legacyKey);
      removedCount++;
      logStorageEvent('debug', 'Removed legacy game state');
    }

    // Clean up old category-specific game states (wallstreetle_gameState_{category})
    const keys = getWallstreetleKeys();
    const gameStateKeys = keys.filter(key => key.includes('_gameState_'));

    gameStateKeys.forEach(key => {
      try {
        const value = localStorage.getItem(key);
        if (!value) return;

        const parsed = JSON.parse(value);
        const gameDate = parsed.gameDate;

        // Remove if game date is not today (or if gameDate is missing)
        if (!gameDate || gameDate !== today) {
          localStorage.removeItem(key);
          removedCount++;
          logStorageBatch('game-state-cleanup', {
            key,
            gameDate: gameDate || 'missing',
            today
          });
        }
      } catch (error) {
        // Invalid/corrupted game state, remove it
        localStorage.removeItem(key);
        removedCount++;
        logStorageEvent('warn', 'Invalid game state removed', {
          key,
          error: error instanceof Error ? error.message : 'Parse error'
        });
      }
    });

    // Clean up wallstreetle_categories storage (contains all category game states)
    try {
      const categoriesKey = 'wallstreetle_categories';
      const categoriesData = localStorage.getItem(categoriesKey);

      if (categoriesData) {
        const parsed = JSON.parse(categoriesData);
        let hasChanges = false;

        // Remove stale game states from each category
        Object.keys(parsed).forEach(category => {
          const categoryData = parsed[category];
          if (categoryData?.gameState) {
            const gameDate = categoryData.gameState.gameDate;

            // Remove category entry if game date is not today (or missing)
            if (!gameDate || gameDate !== today) {
              delete parsed[category];
              hasChanges = true;
              removedCount++;
              logStorageBatch('category-storage-cleanup', {
                category,
                gameDate: gameDate || 'missing',
                today
              });
            }
          }
        });

        // Update or remove the categories storage
        if (hasChanges) {
          if (Object.keys(parsed).length === 0) {
            // All categories cleaned, remove entire key
            localStorage.removeItem(categoriesKey);
            logStorageEvent('debug', 'Removed empty categories storage');
          } else {
            // Some categories remain, update storage
            localStorage.setItem(categoriesKey, JSON.stringify(parsed));
            logStorageEvent('debug', 'Updated categories storage', {
              remainingCategories: Object.keys(parsed)
            });
          }
        }
      }
    } catch (error) {
      logStorageEvent('warn', 'Error cleaning up categories storage', {
        message: error instanceof Error ? error.message : 'Unknown error'
      });
    }

  } catch (error) {
    logStorageEvent('warn', 'Error cleaning up old game states', {
      message: error instanceof Error ? error.message : 'Unknown error'
    });
  }
  return removedCount;
};

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const ensureIsoDate = (date?: string | null): string => {
  if (date && ISO_DATE_REGEX.test(date)) {
    return date;
  }
  return new Date().toISOString().slice(0, 10);
};

/**
 * Remove localStorage entries for feature keys that include game dates in their suffix.
 * Keys should follow the pattern `${prefix}...:${YYYY-MM-DD}` so we can compare dates lexicographically.
 */
export const pruneLocalStorageKeysByDate = (prefix: string, keepDate?: string) => {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return;
  }

  const targetDate = ensureIsoDate(keepDate);
  const keysToRemove: string[] = [];

  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith(prefix)) {
        continue;
      }

      const parts = key.split(':');
      const keyDate = parts[parts.length - 1];
      if (!keyDate || !ISO_DATE_REGEX.test(keyDate)) {
        continue;
      }

      if (keyDate < targetDate) {
        keysToRemove.push(key);
      }
    }
  } catch (error) {
    logStorageEvent('warn', 'Error pruning localStorage keys', {
      prefix,
      message: error instanceof Error ? error.message : 'Unknown error'
    });
  }

  keysToRemove.forEach((key) => {
    try {
      window.localStorage.removeItem(key);
      logStorageBatch('old-key-pruned', { key, targetDate });
    } catch (error) {
      logStorageEvent('warn', 'Failed to remove localStorage key', {
        key,
        prefix,
        message: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  });
};

/**
 * Remove failed submissions older than 24 hours (reduced from 7 days)
 * Also caps maximum queue size to 10 entries per category
 */
export const cleanupOldFailedSubmissions = (): number => {
  let removedCount = 0;
  try {
    const keys = getWallstreetleKeys();
    const failedSubmissionKeys = keys.filter(key => key.includes('_failedSubmissions'));
    const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000); // Changed from 7 days to 24 hours
    const MAX_QUEUE_SIZE = 10; // Cap queue size per category

    failedSubmissionKeys.forEach(key => {
      try {
        const submissions = JSON.parse(localStorage.getItem(key) || '[]');

        // Filter by age and cap size
        let recentSubmissions = submissions.filter((sub: any) => {
          return sub.timestamp && sub.timestamp > oneDayAgo;
        });

        // Cap to max queue size (keep most recent)
        if (recentSubmissions.length > MAX_QUEUE_SIZE) {
          recentSubmissions = recentSubmissions
            .sort((a: any, b: any) => b.timestamp - a.timestamp)
            .slice(0, MAX_QUEUE_SIZE);
        }

        if (recentSubmissions.length < submissions.length) {
          localStorage.setItem(key, JSON.stringify(recentSubmissions));
          removedCount += submissions.length - recentSubmissions.length;
          logStorageBatch('failed-submissions', {
            key,
            removed: submissions.length - recentSubmissions.length
          });
        }
      } catch (error) {
        logStorageEvent('warn', 'Error cleaning failed submissions', {
          key,
          message: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    });
  } catch (error) {
    logStorageEvent('warn', 'Error cleaning up old failed submissions', {
      message: error instanceof Error ? error.message : 'Unknown error'
    });
  }
  return removedCount;
};

/**
 * Remove old daily snapshots (older than 1 day)
 * Snapshots contain large chart data arrays and should be cleaned aggressively
 */
export const cleanupOldSnapshots = (): number => {
  let removedCount = 0;
  try {
    const today = getCurrentGameTimestamp();
    
    // Get all localStorage keys using the proper API
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        keys.push(key);
      }
    }

    // Find all cache keys that look like daily snapshots
    const snapshotKeys = keys.filter(key =>
      key.includes('CategoryDaily') ||
      key.includes('_cache_') ||
      key.includes('snapshot')
    );

    snapshotKeys.forEach(key => {
      try {
        const value = localStorage.getItem(key);
        if (!value) {
          return;
        }

        const parsed = JSON.parse(value);
        const itemDate = parsed.date || parsed.fetchedAt || parsed.timestamp;

        if (itemDate) {
          const itemDay = itemDate.split('T')[0];
          if (itemDay !== today) {
            localStorage.removeItem(key);
            removedCount++;
            logStorageBatch('snapshot-cleanup', {
              key,
              itemDay
            });
          }
        }
      } catch (error) {
        logStorageEvent('warn', 'Invalid snapshot data detected', {
          key,
          message: error instanceof Error ? error.message : 'Unknown error'
        });
        localStorage.removeItem(key);
        removedCount++;
      }
    });
  } catch (error) {
    logStorageEvent('warn', 'Error cleaning up old snapshots', {
      message: error instanceof Error ? error.message : 'Unknown error'
    });
  }
  return removedCount;
};

/**
 * Remove old hint caches (older than current day)
 */
export const cleanupOldHintCaches = (): number => {
  let removedCount = 0;
  try {
    const today = getCurrentGameTimestamp();
    
    // Get all localStorage keys using the proper API
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        keys.push(key);
      }
    }
    const hintCacheKeys = keys.filter(key => key.startsWith('hints-cache-'));

    hintCacheKeys.forEach(key => {
      try {
        const value = localStorage.getItem(key);
        if (value) {
          const parsed = JSON.parse(value);
          const cacheDate = parsed.timestamp || parsed.date;

          if (cacheDate) {
            const cacheDay = cacheDate.split('T')[0];
            if (cacheDay !== today) {
              localStorage.removeItem(key);
              removedCount++;
              logStorageBatch('hint-cleanup', { key, cacheDay });
            }
          }
        }
      } catch (error) {
        // Invalid hint cache, remove it
        localStorage.removeItem(key);
        removedCount++;
        logStorageEvent('warn', 'Invalid hint cache removed', {
          key,
          message: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    });
  } catch (error) {
    logStorageEvent('warn', 'Error cleaning up old hint caches', {
      message: error instanceof Error ? error.message : 'Unknown error'
    });
  }
  return removedCount;
};

/**
 * Remove timer entries older than the current game day
 * Preserves all timers for the current game day across categories
 */
export const cleanupOldTimers = (currentGameDateOverride?: string): number => {
  let removedCount = 0;
  try {
    const currentGameDate = currentGameDateOverride || getCurrentGameTimestamp();
    
    // Get all localStorage keys using the proper API
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        keys.push(key);
      }
    }
    
    // Find all timer keys using the pattern ws:timer:${category}:${gameDate}
    const timerKeys = keys.filter(key => key.startsWith('ws:timer:'));
    
    timerKeys.forEach(key => {
      try {
        // Extract game date from timer key (format: ws:timer:${category}:${gameDate})
        const keyParts = key.split(':');
        if (keyParts.length >= 4) {
          const gameDate = keyParts[3]; // Extract date from key
          
          // Remove timer if it's from a previous game day
          if (gameDate !== currentGameDate) {
            // Validate timer data before removal (optional validation)
            const timerValue = localStorage.getItem(key);
            if (timerValue) {
              try {
                const timerData = JSON.parse(timerValue);
                // Basic validation - check if it has timer-like structure
                if (timerData && typeof timerData === 'object') {
                  // Valid timer structure, safe to remove
                  localStorage.removeItem(key);
                  removedCount++;
                  try {
                    logStorageBatch('timer-cleanup', {
                      key,
                      gameDate,
                      currentGameDate
                    });
                  } catch (logError) {
                    // Ignore logging errors in tests
                  }
                } else {
                  // Invalid timer data, remove it anyway
                  localStorage.removeItem(key);
                  removedCount++;
                  try {
                    logStorageBatch('timer-cleanup', {
                      key,
                      gameDate,
                      reason: 'invalid-timer-data'
                    });
                  } catch (logError) {
                    // Ignore logging errors in tests
                  }
                }
              } catch (parseError) {
                // Invalid JSON, remove the entry
                localStorage.removeItem(key);
                removedCount++;
                try {
                  logStorageEvent('warn', 'Invalid timer JSON removed', {
                    key,
                    message: parseError instanceof Error ? parseError.message : 'JSON parse error'
                  });
                } catch (logError) {
                  // Ignore logging errors in tests
                }
              }
            } else {
              // Empty timer entry, remove it
              localStorage.removeItem(key);
              removedCount++;
              try {
                logStorageBatch('timer-cleanup', {
                  key,
                  reason: 'empty-timer-entry'
                });
              } catch (logError) {
                // Ignore logging errors in tests
              }
            }
          }
        } else {
          // Invalid timer key format, remove it
          localStorage.removeItem(key);
          removedCount++;
          try {
            logStorageEvent('warn', 'Invalid timer key format removed', {
              key,
              reason: 'invalid-key-format'
            });
          } catch (logError) {
            // Ignore logging errors in tests
          }
        }
      } catch (error) {
        try {
          logStorageEvent('warn', 'Error processing timer key', {
            key,
            message: error instanceof Error ? error.message : 'Unknown error'
          });
        } catch (logError) {
          // Ignore logging errors in tests
        }
      }
    });
  } catch (error) {
    try {
      logStorageEvent('warn', 'Error cleaning up old timers', {
        message: error instanceof Error ? error.message : 'Unknown error'
      });
    } catch (logError) {
      // Ignore logging errors in tests
    }
  }
  return removedCount;
};

/**
 * Remove cached stock option lists older than the current day
 */
export const cleanupOldStockOptionCaches = (): number => {
  let removedCount = 0;
  try {
    const today = getCurrentGameTimestamp();
    const keys = getWallstreetleKeys();
    const optionCacheKeys = keys.filter(key => key.startsWith(STOCK_OPTIONS_KEY_PREFIX));

    optionCacheKeys.forEach(key => {
      try {
        const value = localStorage.getItem(key);
        if (!value) {
          return;
        }

        const parsed = JSON.parse(value);
        const timestamp =
          typeof parsed.timestamp === 'number'
            ? parsed.timestamp
            : parsed.fetchedAt
            ? Date.parse(parsed.fetchedAt)
            : null;

        if (!timestamp || Number.isNaN(timestamp)) {
          localStorage.removeItem(key);
          removedCount++;
          logStorageBatch('stock-options-cleanup', {
            key,
            reason: 'invalid-timestamp'
          });
          return;
        }

        const cachedDay = new Date(timestamp).toISOString().split('T')[0];
        if (cachedDay !== today) {
          localStorage.removeItem(key);
          removedCount++;
          logStorageBatch('stock-options-cleanup', {
            key,
            cachedDay
          });
        }
      } catch (error) {
        localStorage.removeItem(key);
        removedCount++;
        logStorageEvent('warn', 'Invalid stock option cache removed', {
          key,
          message: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    });
  } catch (error) {
    logStorageEvent('warn', 'Error cleaning up stock option caches', {
      message: error instanceof Error ? error.message : 'Unknown error'
    });
  }
  return removedCount;
};

/**
 * Trim game results history to last 50 entries (reduced from 100)
 */
export const trimGameResultsHistory = (): number => {
  let removedCount = 0;
  try {
    const key = 'wallstreetle-game-results';
    const stored = localStorage.getItem(key);

    if (stored) {
      const results = JSON.parse(stored);

      if (Array.isArray(results) && results.length > 50) {
        const trimmedResults = results.slice(-50); // Keep last 50
        localStorage.setItem(key, JSON.stringify(trimmedResults));
        removedCount = results.length - 50;
        logStorageBatch('results-trim', {
          removedCount,
          originalSize: results.length
        });
      }
    }
  } catch (error) {
    logStorageEvent('warn', 'Error trimming game results history', {
      message: error instanceof Error ? error.message : 'Unknown error'
    });
  }
  return removedCount;
};

/**
 * Comprehensive cleanup - removes old data and reports savings
 */
export const performStorageCleanup = (): {
  sizeBefore: number;
  sizeAfter: number;
  savedMB: number;
  itemsRemoved: number;
  timersRemoved?: number;
} => {
  const sizeBefore = getLocalStorageSizeMB();
  let itemsRemoved = 0;
  let timersRemoved = 0;

  logStorageEvent('info', 'Storage cleanup started', {
    sizeBeforeMB: Number(sizeBefore.toFixed(2))
  });

  // Clean up old game states
  itemsRemoved += cleanupOldGameStates();

  // Clean up old failed submissions
  itemsRemoved += cleanupOldFailedSubmissions();

  // Clean up old snapshots (CRITICAL - usually largest)
  itemsRemoved += cleanupOldSnapshots();

  // Clean up old hint caches
  itemsRemoved += cleanupOldHintCaches();

  // Clean up stock option caches (daily)
  itemsRemoved += cleanupOldStockOptionCaches();

  // Clean up old timer entries
  timersRemoved = cleanupOldTimers();
  itemsRemoved += timersRemoved;

  // Trim game results history
  itemsRemoved += trimGameResultsHistory();

  const sizeAfter = getLocalStorageSizeMB();
  const savedMB = sizeBefore - sizeAfter;

  logStorageEvent('info', 'Storage cleanup complete', {
    sizeAfterMB: Number(sizeAfter.toFixed(2)),
    savedMB: Number(savedMB.toFixed(2)),
    itemsRemoved,
    timersRemoved
  });

  return {
    sizeBefore,
    sizeAfter,
    savedMB,
    itemsRemoved,
    timersRemoved,
  };
};

/**
 * Emergency cleanup for pre-authentication
 * Aggressively removes non-essential data to ensure enough space for Supabase Auth
 * Target: free at least 1MB of space
 */
export const performEmergencyCleanup = (): {
  success: boolean;
  freedMB: number;
  itemsRemoved: number;
} => {
  const MIN_FREE_SPACE_MB = 1;
  const sizeBefore = getLocalStorageSizeMB();
  let itemsRemoved = 0;

  logStorageEvent('info', 'Emergency cleanup initiated', {
    sizeBeforeMB: Number(sizeBefore.toFixed(2))
  });

  // Step 1: Remove ALL old snapshots
  itemsRemoved += cleanupOldSnapshots();

  // Step 2: Aggressively cap failed submissions to 5 per category
  try {
    const keys = getWallstreetleKeys();
    const failedSubmissionKeys = keys.filter(key => key.includes('_failedSubmissions'));

    failedSubmissionKeys.forEach(key => {
      try {
        const submissions = JSON.parse(localStorage.getItem(key) || '[]');
        if (submissions.length > 5) {
          const recentSubmissions = submissions.slice(-5);
          localStorage.setItem(key, JSON.stringify(recentSubmissions));
          itemsRemoved += submissions.length - 5;
        }
      } catch (error) {
        logStorageEvent('warn', 'Error cleaning failed submissions during emergency cleanup', {
          key,
          message: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    });
  } catch (error) {
    logStorageEvent('warn', 'Error during emergency failed submission cleanup', {
      message: error instanceof Error ? error.message : 'Unknown error'
    });
  }

  // Step 3: Remove ALL hint caches
  try {
    // Get all localStorage keys using the proper API
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        keys.push(key);
      }
    }
    const hintCacheKeys = keys.filter(key => key.startsWith('hints-cache-'));
    hintCacheKeys.forEach(key => {
      localStorage.removeItem(key);
      itemsRemoved++;
    });
    logStorageBatch('hint-cleanup', {
      removed: hintCacheKeys.length,
      mode: 'emergency'
    });
  } catch (error) {
    logStorageEvent('warn', 'Error removing hint caches during emergency cleanup', {
      message: error instanceof Error ? error.message : 'Unknown error'
    });
  }

  // Step 4: Remove stock option caches to force refetch
  try {
    const keys = getWallstreetleKeys();
    const stockOptionKeys = keys.filter(key => key.startsWith(STOCK_OPTIONS_KEY_PREFIX));
    stockOptionKeys.forEach(key => {
      localStorage.removeItem(key);
      itemsRemoved++;
    });
    logStorageBatch('stock-options-cleanup', {
      removed: stockOptionKeys.length,
      mode: 'emergency'
    });
  } catch (error) {
    logStorageEvent('warn', 'Error removing stock option caches during emergency cleanup', {
      message: error instanceof Error ? error.message : 'Unknown error'
    });
  }

  // Step 5: Remove ALL timer entries (aggressive cleanup)
  try {
    // Get all localStorage keys using the proper API
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        keys.push(key);
      }
    }
    const timerKeys = keys.filter(key => key.startsWith('ws:timer:'));
    timerKeys.forEach(key => {
      localStorage.removeItem(key);
      itemsRemoved++;
    });
    if (timerKeys.length > 0) {
      logStorageBatch('timer-cleanup', {
        removed: timerKeys.length,
        mode: 'emergency'
      });
    }
  } catch (error) {
    logStorageEvent('warn', 'Error removing timer entries during emergency cleanup', {
      message: error instanceof Error ? error.message : 'Unknown error'
    });
  }

  // Step 6: Trim game results to 25 entries
  try {
    const key = 'wallstreetle-game-results';
    const stored = localStorage.getItem(key);
    if (stored) {
      const results = JSON.parse(stored);
      if (Array.isArray(results) && results.length > 25) {
        const trimmedResults = results.slice(-25);
        localStorage.setItem(key, JSON.stringify(trimmedResults));
        itemsRemoved += results.length - 25;
      }
    }
  } catch (error) {
    logStorageEvent('warn', 'Error trimming game results during emergency cleanup', {
      message: error instanceof Error ? error.message : 'Unknown error'
    });
  }

  const sizeAfter = getLocalStorageSizeMB();
  const freedMB = sizeBefore - sizeAfter;
  const remainingCapacityMB = MAX_STORAGE_SIZE_MB - sizeAfter;

  logStorageEvent('info', 'Emergency cleanup complete', {
    freedMB: Number(freedMB.toFixed(2)),
    itemsRemoved,
    remainingCapacityMB: Number(remainingCapacityMB.toFixed(2))
  });

  return {
    success: remainingCapacityMB >= MIN_FREE_SPACE_MB,
    freedMB,
    itemsRemoved,
  };
};

/**
 * Display all Wallstreetle localStorage items with their sizes
 */
export const debugLocalStorage = (): void => {
  if (!storageLogger.isEnabled()) {
    return;
  }

  const keys = getWallstreetleKeys();
  const totalSize = getLocalStorageSizeMB();
  const currentGameDate = getCurrentGameTimestamp();

  // Debug regular Wallstreetle keys
  keys.forEach(key => {
    const size = getKeySize(key);
    const sizeMB = size / (1024 * 1024);
    logStorageBatch('storage-debug', {
      key,
      sizeMB: Number(sizeMB.toFixed(4))
    });
  });

  // Debug timer entries separately with additional information
  // Get all localStorage keys using the proper API
  const allKeys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      allKeys.push(key);
    }
  }
  const timerKeys = allKeys.filter(key => key.startsWith('ws:timer:'));
  
  timerKeys.forEach(key => {
    const size = getKeySize(key);
    const sizeMB = size / (1024 * 1024);
    
    // Extract game date from timer key
    const keyParts = key.split(':');
    const gameDate = keyParts.length >= 4 ? keyParts[3] : 'unknown';
    const category = keyParts.length >= 3 ? keyParts[2] : 'unknown';
    const isCurrentDay = gameDate === currentGameDate;
    
    logStorageBatch('timer-debug', {
      key,
      category,
      gameDate,
      isCurrentDay,
      sizeMB: Number(sizeMB.toFixed(4))
    });
  });

  logStorageEvent('info', 'Wallstreetle localStorage summary', {
    totalMB: Number(totalSize.toFixed(2)),
    totalUsedMB: Number(getLocalStorageSizeMB().toFixed(2)),
    storageNearQuota: isStorageNearQuota(),
    timerEntries: timerKeys.length,
    currentGameDate
  });
};
